"use client";

import React from "react";
import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";

/* ---------------- 1. Product Card Skeleton ---------------- */
export const ProductCardSkeleton: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  return (
    <div
      className={`flex flex-col bg-white border border-slate-100 animate-pulse overflow-hidden h-full ${className} ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card}`}
    >
      {/* Square Image Placeholder */}
      <div className="relative aspect-square w-full bg-slate-200/80 p-4" />

      {/* Content Placeholders */}
      <div className="flex flex-1 flex-col p-4 justify-between space-y-3">
        <div className="space-y-2">
          {/* Rating placeholder */}
          <div className="h-3 w-16 bg-slate-200 rounded" />
          {/* Title lines */}
          <div className="h-3.5 w-full bg-slate-200 rounded" />
          <div className="h-3.5 w-3/4 bg-slate-200 rounded" />
        </div>

        {/* Price placeholder */}
        <div className="pt-2 flex items-center justify-between">
          <div className="h-4 w-20 bg-slate-200 rounded" />
          <div className="h-7 w-7 rounded-full bg-slate-200" />
        </div>
      </div>
    </div>
  );
};

/* ---------------- 2. Product Grid Skeleton ---------------- */
export const ProductGridSkeleton: React.FC<{ count?: number }> = ({
  count = 10,
}) => {
  return (
    <div className="grid gap-x-2 md:gap-x-5 gap-y-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 mt-5 mb-5 w-full">
      {Array.from({ length: count }).map((_, idx) => (
        <ProductCardSkeleton key={idx} className="rounded-[8px]" />
      ))}
    </div>
  );
};

/* ---------------- 3. Category / Brand Aspect-Square Card Skeleton ---------------- */
export const CategoryCardSkeleton: React.FC = () => {
  return (
    <div
      className={`relative w-full aspect-square bg-slate-200/80 animate-pulse overflow-hidden ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card}`}
    >
      {/* Top Left Title Bar Placeholder */}
      <div className="absolute top-4 left-4 z-10 w-24 h-4 bg-slate-300/80 rounded" />
    </div>
  );
};

/* ---------------- 4. Order Card Skeleton ---------------- */
export const OrderCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] space-y-4 animate-pulse">
      {/* Header bar */}
      <div className="flex justify-between items-center pb-3 border-b border-slate-100">
        <div className="space-y-1.5">
          <div className="h-3.5 w-24 bg-slate-200 rounded" />
          <div className="h-3 w-32 bg-slate-200 rounded" />
        </div>
        <div className="h-6 w-20 bg-slate-200 rounded-full" />
      </div>

      {/* Product item row */}
      <div className="flex gap-4 items-center">
        <div className="w-16 h-16 bg-slate-200 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-3/4 bg-slate-200 rounded" />
          <div className="h-3 w-1/4 bg-slate-200 rounded" />
        </div>
        <div className="h-4 w-16 bg-slate-200 rounded shrink-0" />
      </div>
    </div>
  );
};

/* ---------------- 5. Order Detail Page Skeleton ---------------- */
export const OrderDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Title & Status Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-2">
          <div className="h-3 w-40 bg-slate-200 rounded" />
          <div className="h-7 w-48 bg-slate-200 rounded" />
        </div>
        <div className="h-8 w-28 bg-slate-200 rounded-full" />
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 bg-white border border-slate-200 rounded-lg">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-16 bg-slate-200 rounded" />
            <div className="h-4 w-24 bg-slate-200 rounded" />
          </div>
        ))}
      </div>

      {/* Delivery Timeline Card */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <div className="h-4 w-36 bg-slate-200 rounded" />
        <div className="flex justify-between items-center pt-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-200" />
              <div className="h-3 w-12 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <div className="h-4 w-32 bg-slate-200 rounded" />
            <div className="h-20 w-full bg-slate-100 rounded-lg" />
            <div className="h-20 w-full bg-slate-100 rounded-lg" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="h-4 w-32 bg-slate-200 rounded" />
          <div className="space-y-2">
            <div className="h-3.5 w-full bg-slate-100 rounded" />
            <div className="h-3.5 w-full bg-slate-100 rounded" />
            <div className="h-3.5 w-2/3 bg-slate-100 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- 6. Wishlist Item Skeleton ---------------- */
export const WishlistItemSkeleton: React.FC = () => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 animate-pulse">
      <div className="flex items-center gap-4 flex-1">
        <div className="w-20 h-20 bg-slate-200 rounded-lg shrink-0" />
        <div className="space-y-2 flex-1">
          <div className="h-3.5 w-20 bg-slate-200 rounded-full" />
          <div className="h-4 w-3/4 bg-slate-200 rounded" />
          <div className="h-4 w-24 bg-slate-200 rounded" />
        </div>
      </div>
      <div className="h-8 w-28 bg-slate-200 rounded-lg shrink-0" />
    </div>
  );
};

/* ---------------- 7. Sidebar Filter Skeleton ---------------- */
export const SidebarSkeleton: React.FC = () => {
  return (
    <div className="w-full bg-white border border-slate-200 rounded-xl p-5 space-y-6 animate-pulse">
      <div className="space-y-3">
        <div className="h-4 w-24 bg-slate-200 rounded" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-4 h-4 bg-slate-200 rounded" />
            <div className="h-3.5 w-28 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
      <div className="space-y-3 pt-4 border-t border-slate-100">
        <div className="h-4 w-20 bg-slate-200 rounded" />
        <div className="h-6 w-full bg-slate-200 rounded" />
      </div>
    </div>
  );
};
